import { PaginationOrder } from '@inappsis/react-inappsis-ui';

export type PurchasesListState = {
  searchValue: string;
  limit: number;
  offset: number;
  order: PaginationOrder;
  orderBy: string;
};

export const purchasesListDefaults: PurchasesListState = {
  searchValue: '',
  limit: 10,
  offset: 0,
  order: 'desc',
  orderBy: 'latest_order_created_at'
};

const prefix = 'toolit:purchases-list:v1:';
const validUser = (userId?: number): userId is number =>
  Number.isSafeInteger(userId) && (userId ?? 0) > 0;

export const readPurchasesListState = (
  userId: number | undefined,
  sortableFields: string[]
): PurchasesListState => {
  if (!validUser(userId)) return { ...purchasesListDefaults };
  try {
    const raw = sessionStorage.getItem(`${prefix}${userId}`);
    const state = raw ? JSON.parse(raw) : null;
    if (
      state &&
      typeof state.searchValue === 'string' &&
      [10, 25, 50, 100].includes(state.limit) &&
      Number.isSafeInteger(state.offset) &&
      state.offset >= 0 &&
      state.offset % state.limit === 0 &&
      ['asc', 'desc'].includes(state.order) &&
      sortableFields.includes(state.orderBy)
    ) {
      return {
        searchValue: state.searchValue.trim(),
        limit: state.limit,
        offset: state.offset,
        order: state.order,
        orderBy: state.orderBy
      };
    }
  } catch {
    // Corrupt or unavailable browser storage must not prevent using the list.
  }
  return { ...purchasesListDefaults };
};

export const savePurchasesListState = (
  userId: number | undefined,
  { searchValue, limit, offset, order, orderBy }: PurchasesListState
) => {
  if (!validUser(userId)) return;
  try {
    sessionStorage.setItem(
      `${prefix}${userId}`,
      JSON.stringify({ searchValue, limit, offset, order, orderBy })
    );
  } catch {
    // The current list still works if storage is blocked or full.
  }
};

export const clearPurchasesListState = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Logging out must also work when browser storage is unavailable.
  }
};
